import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from "react";
import { usePostEditor } from "./editor/usePostEditor.js";
import { useRoute } from "../routing/index.js";
import { addBlockMetadata } from "./block-utils.js";
const InlineEditingContext = createContext(undefined);
export function InlineEditingContextProvider(props) {
    const parentContext = useContext(InlineEditingContext);
    useMemo(() => {
        addBlockMetadata(props.innerBlocks ?? []);
    }, [props.innerBlocks]);
    return (_jsx(InlineEditingContext.Provider, { value: {
            values: props.values,
            innerBlocks: props.innerBlocks,
            block: props.block,
            index: props.index,
            parents: [...(parentContext?.block ? [parentContext?.block] : []), ...(parentContext?.parents || [])].filter(Boolean),
            getValue(key) {
                return props.values?.[key];
            },
            setValue(key, value) {
                if (props.onChange) {
                    props.onChange({
                        ...(props.values || {}),
                        [key]: value,
                    });
                }
            },
            insertBlocksAfter: props.insertBlocksAfter,
            readonly: props.readonly || parentContext?.readonly,
        }, children: props.children }));
}
export function ReadonlyInlineEditingProvider(props) {
    const parentContext = useContext(InlineEditingContext);
    return (_jsx(InlineEditingContext.Provider, { value: {
            ...parentContext,
            readonly: true,
        }, children: props.children }));
}
/**
 * Allows for arbitrary storage of key/value pairs within the current block.
 * This is used internally by EditableText, but can be used to store anything.
 *
 * @param key The unique item identifier
 * @param defaultValue The default value (default is undefined)
 * @returns a [value, setValue] tuple, similar to useState
 */
export function useInlineEditableValue(key, defaultValue) {
    const attrs = useContext(InlineEditingContext);
    return [
        attrs?.getValue(key) ?? defaultValue,
        (value) => {
            attrs?.setValue(key, value);
        },
    ];
}
/**
 * Can be used as a drop-in version of useInlineEditableValue, but will pipe-through a store if one has been used
 * @private
 **/
export function useValueStore(id, defaultValue) {
    const attrs = useContext(InlineEditingContext);
    if (typeof id === "string") {
        return [
            attrs?.getValue(id) ?? defaultValue,
            (value) => {
                attrs?.setValue(id, value);
            },
        ];
    }
    else if (Array.isArray(id)) {
        return [id[0] ?? defaultValue, id[1]];
    }
    else {
        throw new Error("Missing ID or store for inline value");
    }
}
export function useBlockAppender() {
    const ctx = useContext(InlineEditingContext);
    return ctx?.insertBlocksAfter;
}
export function useInnerBlocks() {
    const ctx = useContext(InlineEditingContext);
    return ctx?.innerBlocks || [];
}
export function useBlockParents() {
    const ctx = useContext(InlineEditingContext);
    return ctx?.parents || [];
}
export function useBlockContext() {
    return useContext(InlineEditingContext);
}
export function useBlockSelection() {
    if (env.admin) {
        return wp.data.useSelect((select) => {
            // @ts-ignore
            return select("core/block-editor").getSelectedBlock();
        }, []);
    }
}
/**
 * Returns the current template name.
 * - In the editor, this uses Gutenberg state.
 * - On the frontend, this uses the actual WP template hierarchy.
 *
 * As such, there may be inconsistencies between the editor and the frontend.
 */
export function useTemplate() {
    if (env.admin) {
        const post = usePostEditor();
        if (post.post?.template) {
            return post.post?.template?.replace(/(views\/|\.tsx$)/g, "");
        }
        else if (post.post?.type === "page") {
            return "page";
        }
        else {
            return ("single-" + post.post?.type);
        }
    }
    else {
        return useRoute().view;
    }
}
